import classNames from 'classnames';
import { WithRouterProps } from 'next/dist/client/with-router';
import dynamic from 'next/dynamic';
import { SingletonRouter, withRouter } from 'next/router';
import * as React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { UserContext } from '../../../contexts/UserContext';
import Merchant from '../../../domain/entities/Merchant';
import { reservationUrlBy } from './helper';

import css from './MerchantStickyFooter.module.scss';

interface MerchantFavoriteButtonProps {
  className?: string;
  publicId: string;
  isLoggedIn: boolean;
}

const MerchantFavoriteButton = dynamic<MerchantFavoriteButtonProps>(
  import('./MerchantFavoriteButtonInStickyFooter').then(c => c.default),
  {
    loading: () => null,
    ssr: false,
  },
);

const bookingLinkIsActiveBy = (
  merchant: Merchant,
  bookingPageExist: boolean,
  bookingPageIsBookable?: boolean,
) => {
  // explore only merchant.
  if (
    merchant.setting.isExploreOnly &&
    merchant.setting.exploreOnlyMerchantReservationPageUrl !== ''
  ) {
    return true;
  }
  // bookable merchant on coubic.
  if (bookingPageExist && bookingPageIsBookable !== false) {
    return true;
  }
  // others.
  return false;
};

interface OwnProps {
  router?: SingletonRouter;
  merchant: Merchant;
  bookingPageCount: number;
  bookingPageIsBookable?: boolean;
}

type Props = OwnProps & InjectedIntlProps & WithRouterProps;

const MerchantStickyFooter = ({
  merchant,
  intl,
  router,
  bookingPageCount,
  bookingPageIsBookable,
}: Props) => {
  const phoneNumber = merchant.phoneNumber;
  const phoneClasses = classNames(css.MerchantStickyFooter__Link, {
    [css.MerchantStickyFooter__Disabled]: phoneNumber === '',
  });
  const bookingLinkIsActive = bookingLinkIsActiveBy(
    merchant,
    bookingPageCount > 0,
    bookingPageIsBookable,
  );
  const reservationClasses = classNames(css.MerchantStickyFooter__Link, {
    [css.MerchantStickyFooter__Disabled]: !bookingLinkIsActive,
  });
  const userCtx = React.useContext(UserContext);
  const reservationUrl = reservationUrlBy(merchant, router);
  return (
    <div className={css.MerchantStickyFooter}>
      <a href={`tel:${phoneNumber}`} className={phoneClasses}>
        <span className={css.MerchantStickyFooter__Image}>
          <svg>
            <use xlinkHref="#call_to_action_phone" />
          </svg>
        </span>
        <span className={css.MerchantStickyFooter__Text}>
          {intl.formatMessage({
            id: 'components.common.merchantStickyFooter.phone',
          })}
        </span>
      </a>
      <a
        href={reservationUrl.url}
        target={reservationUrl.targetBlank ? '_blank' : undefined}
        className={reservationClasses}
      >
        <span className={css.MerchantStickyFooter__Image}>
          <svg>
            <use xlinkHref="#call_to_action_schedule" />
          </svg>
        </span>
        <span className={css.MerchantStickyFooter__Text}>
          {intl.formatMessage({
            id: 'components.common.merchantStickyFooter.reservation',
          })}
        </span>
      </a>
      <span className={css.MerchantStickyFooter__Link}>
        <span className={css.MerchantStickyFooter__Image}>
          <MerchantFavoriteButton
            publicId={merchant.publicId}
            isLoggedIn={userCtx.auth.isLoggedIn}
          />
        </span>
        <span className={css.MerchantStickyFooter__Text}>
          {intl.formatMessage({
            id: 'components.common.merchantStickyFooter.favorite',
          })}
        </span>
      </span>
    </div>
  );
};

export default injectIntl(withRouter(MerchantStickyFooter));
